import { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import Layout from '@/components/layouts/Layout';

import { highlightedPageLimit, pageLimit, revalidateTimeout } from '@/lib/constants';
import { drupal } from '@/utils/drupal/drupal';
import { getGlobalElements } from '@/utils/drupal/get-global-elements';
import { getParams } from '@/utils/drupal/get-params';
import { getResourcePath } from '@/utils/drupal/get-resource-path';
import getContent from '@/utils/drupal/get-content';
import Container from '@/atoms/container';
import Content01 from '@/atoms/content-01';
import SliderHero from '@/organisms/slider-hero';
import Hero from '@/molecules/hero';
import { ButtonVariant } from '@/atoms/button/types';
import { getUrlFromMedia } from '@/utils/utils';
import { ProductBlockShopify } from '@/templates/product-block-shopify/ProductBlockShopify';
import { StylePagination } from '@/organisms/slider-hero/types';
import { DrupalNode, DrupalTaxonomyTerm, deserialize } from 'next-drupal';
import { DrupalItemType, ItemListFilter } from '@/organisms/item-list-filter/ItemListFilter';
import { recipeFilters } from '@/lib/filters';
import { useMemo } from 'react';
import { NodePageProps } from 'pages/[...slug]';

export default function Recetas({
    content,
    blocks,
    theme,
    preFooter,
    meta,
    canonical,
}: NodePageProps) {
    const heroPosts = content.views.highlightedContent;
    const recipeCategories = content.views.categoryContent;
    useMemo(() => (
        recipeFilters.find((filter) => filter.label == "Categoría").list = recipeCategories.map((category) => (
            { key: category.name, value: category.drupal_internal__tid }
        ))
    ), [recipeCategories]);
    return (
        <Layout blocks={blocks} theme={theme ? 'dark' : 'light'} layoutType='base' preFooter={preFooter} meta={meta} canonical={canonical}>
            <div className="pt-header">
                <Container wrapper wrapperSize="max-w-[790px]">
                    <Content01
                        pretitle="ESCUELA DEL JAMÓN"
                        title="Recetas"
                        typeTitle="h1"
                        position="center"
                    />
                </Container>

                <Container wrapper>
                    <SliderHero pagination={StylePagination.ARTICLES_PAGINATION} className={`${heroPosts.length > 1 ? "md:mb-[30px]" : ""}`}>
                        {heroPosts.map((post: any, index: number) => (
                            <SliderHero.Slide key={index}>
                                <Hero
                                    backgroundImage={getUrlFromMedia(post.field_image)}
                                    overlay={0.5}
                                    theme="dark"
                                    className="!p-10"
                                >
                                    <Hero.Title>{post.title}</Hero.Title>
                                    <Hero.Button
                                        href={post.path.alias}
                                        variant={ButtonVariant.DISPLAY}
                                    >
                                        Ver post
                                    </Hero.Button>
                                </Hero>
                            </SliderHero.Slide>
                        ))}
                    </SliderHero>
                </Container>
                <ItemListFilter filters={recipeFilters} content={content.views.content} facetCount={content.facetCount} type={DrupalItemType.RECIPE} />
                <Container wrapper>
                    <ProductBlockShopify
                        shopifyCategory="destacados"
                        shopifyProducts={content.views.products}
                        title="Te puede interesar"
                    />
                </Container>
            </div >
        </Layout >
    );
};

export async function getStaticProps(
    context: GetStaticPropsContext
): Promise<GetStaticPropsResult<NodePageProps>> {
    const slug = "/es/escuela-del-jamon/recetas";
    const path = await drupal.translatePath(slug);
    const resource = await getResourcePath(path, context);
    if (resource == null) {
        return {
            notFound: true,
            revalidate: revalidateTimeout
        };

    }
    const type = path.jsonapi?.resourceName;
    const contentObject = await getContent(resource, type, context);

    contentObject.content.views.highlightedContent = await drupal.getResourceCollectionFromContext<DrupalNode[]>(
        "node--recipe", context, {
        params: getParams("node--recipe", "card")
            .addFilter("promote", "1")
            .addSort("created", "DESC")
            .addPageLimit(highlightedPageLimit)
            .getQueryObject(),
    });

    const recipes = await drupal.getSearchIndex<DrupalNode[]>('recipes', {
        deserialize: false,
        params: getParams("node--recipe", "card")
            .addSort("created", "DESC")
            .addPageLimit(pageLimit)
            .getQueryObject()
    });
    contentObject.content.views.content = deserialize(recipes) as DrupalNode[];
    contentObject.content.facetCount = recipes.meta.count;

    contentObject.content.views.categoryContent = await drupal.getResourceCollectionFromContext<DrupalTaxonomyTerm[]>(
        "taxonomy_term--recipe_category", context, {
        params: getParams("taxonomy_term--recipe_category")
            .getQueryObject(),
    });
    return {
        props: {
            ...(await getGlobalElements(context)),
            content: contentObject.content,
            meta: contentObject.content.node.metatag ? contentObject.content.node.metatag : null,
            theme: contentObject.content.node.field_dark_banner,
            preFooter: contentObject.content.node.field_show_prefooter,
            canonical: path.entity.path ? path.entity.path : slug,
        },
        revalidate: revalidateTimeout
    };
};
